import client from './client';

// Track authentication state
let authState = {
  username: localStorage.getItem('username'),
  isAuthenticated: false,
  isLoading: true
};

// Event to notify components of auth state changes
const notifyAuthChange = () => {
  window.dispatchEvent(new CustomEvent('auth-state-change', { 
    detail: { ...authState }
  }));
};

export const login = async (username, password) => {
  try {
    const response = await client.post('/api/users/login',
      { username, password },
      { withCredentials: true }
    );

    if (response.status >= 200 && response.status < 300 && response.data.username) {
      authState = {
        ...authState,
        username: response.data.username,
        isAuthenticated: true
      };
      localStorage.setItem('username', response.data.username);
      notifyAuthChange();
      return response.data;
    }

    // Non-2xx or missing username — treat as failed login
    authState = { ...authState, username: null, isAuthenticated: false };
    localStorage.removeItem('username');
    notifyAuthChange();
    const err = new Error('Login failed');
    err.customMessage = 'Kullanıcı adı veya şifre hatalı';
    err.response = response;
    throw err;
  } catch (error) {
    if (error.customMessage) throw error; // already formatted above

    authState = { ...authState, username: null, isAuthenticated: false };
    localStorage.removeItem('username');
    notifyAuthChange();

    if (!navigator.onLine) {
      error.customMessage = 'İnternet bağlantınızı kontrol edin';
    } else {
      error.customMessage = 'Kullanıcı adı veya şifre hatalı';
    }
    throw error;
  }
};

export const signup = async (username, password) => {
  try {
    const response = await client.post('/api/users',
      { username, password },
      { withCredentials: true }
    );

    if (response.status >= 200 && response.status < 300 && response.data.username) {
      authState = {
        ...authState,
        username: response.data.username,
        isAuthenticated: true
      };
      localStorage.setItem('username', response.data.username);
      notifyAuthChange();
      return response.data;
    }

    authState = { ...authState, username: null, isAuthenticated: false };
    localStorage.removeItem('username');
    notifyAuthChange();
    const err = new Error(response.data?.message || 'Signup failed');
    err.response = response;
    throw err;
  } catch (error) {
    if (error.response?.data?.message && !error.message.startsWith('Signup')) {
      // already formatted
    } else {
      authState = { ...authState, username: null, isAuthenticated: false };
      localStorage.removeItem('username');
      notifyAuthChange();
    }
    throw error;
  }
};

export const logout = async () => {
  try {
    // Send logout request with credentials to ensure cookies are included
    await client.post('/api/users/logout', {}, { 
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Clear OAuth2 and JWT cookies
    const cookies = ['JSESSIONID', 'OAUTH2_AUTHORIZATION_REQUEST', 'oauth2_auth_request', 'redirect_uri', 'AUTH-TOKEN'];
    cookies.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Clear all auth-related data regardless of logout request success
    authState = {
      ...authState,
      username: null,
      isAuthenticated: false
    };
    // Preserve user-preference keys that should survive logout.
    const PRESERVE_KEYS = ['mizan.onboarding.seen.v1', 'mizan.theme'];
    const preserved = {};
    PRESERVE_KEYS.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v !== null) preserved[k] = v;
    });
    localStorage.clear();
    Object.entries(preserved).forEach(([k, v]) => localStorage.setItem(k, v));
    sessionStorage.clear(); // Also clear session storage which might contain OAuth2 state
    
    // Force a check with the backend to ensure session is cleared
    await checkAuthStatus();
    notifyAuthChange();
    
    // Reload the page to ensure complete cleanup of OAuth2 state
    window.location.reload();
  }
};

// Verify authentication status with backend
export const checkAuthStatus = async () => {
  try {
    const response = await client.get('/api/users/me', { withCredentials: true });
    if (response.status === 200 && response.data.username) {
      authState = {
        ...authState,
        username: response.data.username,
        isAuthenticated: true
      };
      localStorage.setItem('username', response.data.username);
      notifyAuthChange();
      return true;
    }
    // 401 or unexpected response — not authenticated
    authState = { ...authState, username: null, isAuthenticated: false };
    localStorage.removeItem('username');
    notifyAuthChange();
    return false;
  } catch (error) {
    authState = {
      ...authState,
      username: null,
      isAuthenticated: false
    };
    localStorage.removeItem('username');
    notifyAuthChange();
    return false;
  }
};

export const isAuthenticated = () => {
  return authState.isAuthenticated;
};

export const isAuthLoading = () => {
  return authState.isLoading;
};

export const getUsername = () => {
  return authState.username;
};

// Initialize auth state
checkAuthStatus()
  .finally(() => {
    authState.isLoading = false;
    notifyAuthChange();
  })
  .catch(console.error);
